import type { FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import Navigation from '@segment/templates/Navigation'
import Footer from '@segment/templates/Footer'

import { useArticleQuery } from '@segment/api/routes/dataHub/queries'

import useRichTextTableOfContent, { getNodeText } from '@segment/utils/useRichTextTableOfContent'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Sidebar from './components/Sidebar'
import Content from './components/Content'
import Form from './components/Form'
import Faq from './components/Faq'
import BackTo from './components/BackTo'

export interface DataHubArticleProps {
  articleSlug: string
  categorySlug: string
}

const DataHubArticle: FC<PropsWithChildren<DataHubArticleProps>> = ({ articleSlug, categorySlug }) => {
  const { data } = useArticleQuery({ args: [articleSlug, categorySlug] })
  const theme = useTheme()
  const tableOfContentState = useRichTextTableOfContent(data?.mainContent)

  const contentHeadingsOptions = useMemo(
    () => tableOfContentState.getMenuLevelItems(0).map(([node, id]) => ({ text: getNodeText(node as any), value: id })),
    [tableOfContentState]
  )

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={`${data?.seoTitle} | Twilio Segment`}
        pageDescription={data?.seoDescription}
        pageOG={data?.seoImage}
        bodyBackground={theme.palette.neutrals.white[100]}
      >
        <Navigation />
        <Main>
          <Hero title={data?.title} description={data?.description} />
          <StyledSectionBase variant="sidebar" sidebarSize="small">
            <StyledSectionSidebar>
              {contentHeadingsOptions && <Sidebar id="article-sidebar" data={contentHeadingsOptions} />}
            </StyledSectionSidebar>
            <Section.Contents>
              {data?.mainContent && <Content data={data.mainContent} tableOfContentState={tableOfContentState} />}
              <Form />
              {data?.faqs && <Faq data={data?.faqs} />}
            </Section.Contents>
          </StyledSectionBase>
          {data?.category && <BackTo categorySlug={data.category?.slug} categoryName={data.category?.title} />}
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

export default DataHubArticle

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

const StyledSectionBase = styled(SectionBase)`
  overflow-x: initial;
`

const StyledSectionSidebar = styled(Section.Sidebar)`
  display: grid;
`
